import type {
  LifeReadinessCategory,
  LifeReadinessQuestion,
  LifeReadinessQuestionUserLevel,
} from '@backrs_ui/common-ui';

import { useRegisterAgentContext } from '@backrs_ui/grow-ui/src/agentContext/useRegisterAgentContext';

type LevelLabels = Readonly<{
  notYet: { title: string; tooltip: string };
  early: { title: string; tooltip: string };
  strong: { title: string; tooltip: string };
  advanced: { title: string; tooltip: string };
}>;

const LEVEL_NUMBER_TO_KEY: Record<number, keyof LevelLabels> = {
  1: 'notYet',
  2: 'early',
  3: 'strong',
  4: 'advanced',
};

export const useAgentContext = ({
  showExplainerCard,
  categories,
  foundCategory,
  questionsForSubcategory,
  userLevels,
  currentPage,
  totalPages,
  levelLabels,
}: {
  showExplainerCard: boolean;
  categories: LifeReadinessCategory[];
  foundCategory: LifeReadinessCategory;
  questionsForSubcategory: LifeReadinessQuestion[];
  userLevels: LifeReadinessQuestionUserLevel[];
  currentPage: number;
  totalPages: number;
  levelLabels: LevelLabels;
}) => {
  useRegisterAgentContext({
    kind: 'form',
    description: showExplainerCard
      ? 'Showing the introductory explainer card before the quiz begins'
      : `Answering questions in the "${foundCategory.name}" category`,
    data: {
      showing_explainer: showExplainerCard,
      current_category: foundCategory.name,
      total_categories: categories.length,
      answer_levels: [
        { level: levelLabels.notYet.title, description: levelLabels.notYet.tooltip },
        { level: levelLabels.early.title, description: levelLabels.early.tooltip },
        { level: levelLabels.strong.title, description: levelLabels.strong.tooltip },
        { level: levelLabels.advanced.title, description: levelLabels.advanced.tooltip },
      ],
      questions_on_page: questionsForSubcategory.length,
      questions_answered: questionsForSubcategory.filter((q) =>
        userLevels.some((ul) => ul.life_survey_question_id === q.id),
      ).length,
      current_page: currentPage,
      total_pages: totalPages,
    },
  });

  const userLevelMap = new Map(userLevels.map((ul) => [ul.life_survey_question_id, ul.level]));

  useRegisterAgentContext({
    kind: 'list',
    description: showExplainerCard
      ? 'No questions visible — showing introductory explainer card'
      : 'Self-assessment questions in the current category, each with a title, description, and four selectable answer levels',
    data: {
      visible: !showExplainerCard,
      questions: questionsForSubcategory.map((q) => {
        const numericLevel = userLevelMap.get(q.id);
        const levelKey = numericLevel ? LEVEL_NUMBER_TO_KEY[numericLevel] : undefined;

        return {
          name: q.name,
          description: q.description,
          levels: {
            [levelLabels.notYet.title]: q.level_one,
            [levelLabels.early.title]: q.level_two,
            [levelLabels.strong.title]: q.level_three,
            [levelLabels.advanced.title]: q.level_four,
          },
          selectedLevel: levelKey ? levelLabels[levelKey].title : null,
        };
      }),
    },
  });
};
